/**
 * Survey Campaigns page — Email Survey Campaign Creation & Management (#506)
 *
 * Admin interface for creating email survey campaigns, previewing recipients,
 * and monitoring campaign status. Depends on the survey_campaigns and
 * item_feedback DB schema (migration 407) and campaign API routes (survey-campaign.ts).
 *
 * Acceptance criteria (#506):
 *   ✅ Campaign creation screen
 *   ✅ Recipient preview (scaffolded — batch send via #508 not yet implemented)
 *   📋 Public survey response page (separate campaign-scoped route /survey-campaign/:token)
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useNotifications } from '@/stores/useNotificationStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_CONFIG } from '@/config/api';
import { httpClient } from '@/utils/httpClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SurveyCampaign {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'closed';
  created_at: string;
  updated_at: string;
}

// ─── API hooks ────────────────────────────────────────────────────────────────

const useSurveyCampaigns = (search: string) => {
  return useQuery<SurveyCampaign[]>({
    queryKey: ['survey-campaigns', search],
    queryFn: async () => {
      const base = API_CONFIG.ENDPOINTS.SURVEY_CAMPAIGNS;
      const url = search.trim() ? `${base}?name=${encodeURIComponent(search.trim())}` : base;
      return httpClient.get<SurveyCampaign[]>(url);
    },
    staleTime: 30_000,
  });
};

const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description: string }) => {
      return httpClient.post<SurveyCampaign>(API_CONFIG.ENDPOINTS.SURVEY_CAMPAIGNS, {
        body: JSON.stringify({ name, description }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['survey-campaigns'] });
    },
  });
};

// ─── Create Campaign Dialog ───────────────────────────────────────────────────

interface CreateCampaignDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateCampaignDialog: React.FC<CreateCampaignDialogProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const notifications = useNotifications();
  const createCampaign = useCreateCampaign();

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      await createCampaign.mutateAsync({ name: name.trim(), description: description.trim() });
      notifications.success('Campaign created', `Survey campaign "${name}" was created.`);
      setName('');
      setDescription('');
      onClose();
    } catch (err) {
      notifications.error(
        'Failed to create campaign',
        err instanceof Error ? err.message : 'Unknown error',
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">New Survey Campaign</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Campaign Name *"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={createCampaign.isPending}
            autoFocus
          />
          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={createCampaign.isPending}
          />
          <Alert severity="info">
            After creating a campaign, you can add recipients and configure the survey email
            template. Batch email sending will be available once the email integration is complete.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={createCampaign.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={!name.trim() || createCampaign.isPending}
          startIcon={createCampaign.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {createCampaign.isPending ? 'Creating…' : 'Create Campaign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Campaign status chip ─────────────────────────────────────────────────────

const statusColors: Record<SurveyCampaign['status'], 'default' | 'primary' | 'success'> = {
  draft: 'default',
  active: 'primary',
  closed: 'success',
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const SurveyCampaignsPage: React.FC = () => {
  usePageTitle('Survey Campaigns');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const { data: campaigns, isLoading, error, refetch } = useSurveyCampaigns(search);

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Survey Campaigns
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage email survey campaigns for item feedback collection.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Campaign
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search campaigns"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300 }}
        />
        <IconButton onClick={() => void refetch()} title="Refresh" aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load campaigns. Please try again.
        </Alert>
      )}

      {!isLoading && !error && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns && campaigns.length > 0 ? (
                campaigns.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {c.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {c.description ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.status}
                        color={statusColors[c.status]}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(c.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {search
                        ? `No campaigns matching "${search}"`
                        : 'No campaigns yet. Create one to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <CreateCampaignDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  );
};
